import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, FileStack, Hourglass, Layers } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { PageHeader, Loading, ErrorBox, StatusBadge, fmtDate } from "../components/common";

const Kpi = ({ icon: Icon, label, value, testid, accent }) => (
  <div className="rounded-md border bg-white p-5 rise" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="overline-label">{label}</p>
      <Icon className={`h-4 w-4 ${accent ? "text-brand2" : "text-brand"}`} />
    </div>
    <p className="mt-3 text-3xl font-bold font-display">{value}</p>
  </div>
);

export default function Dashboard() {
  const { meta } = useApp();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = () => { setErr(""); api.get("/dashboard").then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); };
  useEffect(load, []);
  const modMeta = (k) => meta.modules.find((m) => m.key === k);

  if (err) return <ErrorBox text={err} onRetry={load} />;
  if (!d) return <Loading />;
  const total = d.modules.reduce((a, m) => a + m.total, 0);
  const pending = d.modules.reduce((a, m) => a + (m.by_status[m.initial] || 0), 0);

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`Magandang araw, ${meta.user.name.split(" ")[0]}`} subtitle={`${meta.branding.lgu_name} · Signed in as ${meta.user.role_label}. Figures reflect only the modules your role can access.`} />
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi icon={Layers} label="Accessible modules" value={meta.modules.length} testid="kpi-modules" />
        <Kpi icon={FileStack} label="Records in scope" value={total} testid="kpi-records" />
        <Kpi icon={Hourglass} label="Awaiting first action" value={pending} testid="kpi-pending" accent />
        <Kpi icon={AlertTriangle} label="Overdue compliance" value={d.compliance_overdue ?? "—"} testid="kpi-compliance-overdue" accent />
      </div>
      <div className="mt-8 grid xl:grid-cols-[2fr_1fr] gap-6">
        <section>
          <h2 className="text-lg font-semibold mb-3">Module workload</h2>
          {d.modules.length === 0 && <p className="text-sm text-slate-500">No record modules are assigned to your role.</p>}
          <div className="grid sm:grid-cols-2 gap-3">
            {d.modules.map((m) => {
              const mm = modMeta(m.key);
              return (
                <Link key={m.key} to={`/m/${m.key}`} className="rounded-md border bg-white p-4 hover:border-[var(--brand-primary)] transition-colors" data-testid={`dashboard-module-${m.key}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-sm"><span className="font-mono text-xs text-slate-400 mr-1">{String(m.no).padStart(2, "0")}</span>{m.name}</p>
                    <span className="font-display font-bold text-lg">{m.total}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {Object.entries(m.by_status).map(([s, c]) => (
                      <span key={s} className="flex items-center gap-1 text-xs"><StatusBadge status={s} statuses={mm?.statuses} /> {c}</span>
                    ))}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
        <section>
          <h2 className="text-lg font-semibold mb-3">Recent activity</h2>
          <div className="rounded-md border bg-white divide-y" data-testid="recent-activity">
            {d.recent.length === 0 && <p className="p-4 text-sm text-slate-500">No recent records.</p>}
            {d.recent.map((r) => (
              <Link key={r.id} to={`/m/${r.module}?open=${r.id}`} className="block p-3 hover:bg-slate-50">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-slate-500">{r.ref_no}</span>
                  <StatusBadge status={r.status} statuses={modMeta(r.module)?.statuses} />
                </div>
                <p className="text-sm mt-1 truncate">{r.title}</p>
                <p className="text-xs text-slate-500">{fmtDate(r.updated_at)}</p>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
